// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import analyticstracker from "@jmc/analyticstracker";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { Icon } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { jnjMail, jnjPhone } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { keyHandler } from "@jmc/utils/utils/keyHandler";
import { mdiEmailOutline, mdiPhone } from "@mdi/js";
import classnames from "classnames";
import React from "react";

import { determineUrl } from "../../../utils/determineUrl";
import { getMarkerId } from "../Geo";
import { useLocatorContext } from "../LocatorContext/LocatorContext";
import { LocationData } from "../types/LocationData";
import { showIcon } from "../utils/showIcon";
import style from "./style.module.scss";

interface HitLocation {
    hit: LocationData;
    data?: string;
    name?: string;
    placement?: string;
}

export const Hit = ({ hit, name, placement }: HitLocation): JSX.Element => {
    const locale = useLocale() || "en-us";
    const { jnjFullBranded } = useJnjBranding();
    const { activeHit, setTrackingName } = useLocatorContext();
    const selected = activeHit && hit?.id === activeHit?.id;

    const selectLocation = ({
        targetType,
        targetUrl,
        event,
    }: {
        targetType: "external" | "internal" | "mailto" | "callto" | "print";
        targetUrl?: string;
        event: MouseEvent;
    }): void => {
        if (event && event.stopPropagation) event.stopPropagation();
        analyticstracker().trackEvent({
            event: EventTypes.MAP_CLICK,
            info: {
                name: name,
                component: "list",
                placement: placement,
                targettype: targetType,
                targeturl: determineUrl({ url: targetUrl, locale: locale, external: false }),
            },
        });

        // Get the marker on the map and click it.
        const elem = document?.getElementById(getMarkerId(hit.objectID));
        elem && elem.click();
    };

    const icon = showIcon(hit?.type, jnjFullBranded);

    return (
        <div
            className={classnames(style.result, selected ? style.selected : null)}
            onClick={(e) => {
                selectLocation({ targetType: "internal", event: e as unknown as MouseEvent });
                setTrackingName("list");
            }}
            onKeyDown={(e: KeyboardEvent): void =>
                keyHandler(e.key, () => {
                    selectLocation({ targetType: "internal", event: e as unknown as MouseEvent });
                    setTrackingName("list");
                })
            }
            tabIndex={0}
            role="button"
        >
            <div className={style.iconBackground}>
                <div className={style.resultIcon}>
                    <Icon size="large" color="white" icon={icon} />
                </div>
            </div>
            <div className={style.resultInfo}>
                <Typography
                    font="text"
                    variant="body"
                    size="m"
                    weight="400"
                    lineHeight="l"
                    className={jnjFullBranded ? style.name : null}
                >
                    {hit?.name}
                </Typography>
                {hit.center_unit && (
                    <Typography font="text" variant="body" size="s" color="light" lineHeight="l">
                        {hit?.center_unit}
                    </Typography>
                )}
                <Typography font="text" variant="body" size="s" color="light" lineHeight="l">
                    {hit?.specialty}
                </Typography>
            </div>
            <div className={style.icons}>
                <a
                    href={hit?.email ? `mailto:${hit?.email}` : "#"}
                    onClick={(e) => {
                        selectLocation({
                            targetType: "mailto",
                            targetUrl: `mailto:${hit?.email}`,
                            event: e as unknown as MouseEvent,
                        });
                        !hit?.email && e.preventDefault();
                        setTrackingName("list");
                    }}
                    className={classnames(style.iconWrapper, hit?.email ? "" : style.disabledWrapper)}
                >
                    <Icon
                        color="inherit"
                        size={jnjFullBranded ? "medium" : "xs"}
                        icon={jnjFullBranded ? jnjMail : mdiEmailOutline}
                        type={jnjFullBranded ? "jnj" : "mdi"}
                    />
                </a>
                <a
                    href={hit?.phone ? `tel:${hit?.phone}` : "#"}
                    onClick={(e) => {
                        selectLocation({
                            targetType: "callto",
                            targetUrl: `tel:${hit?.phone}`,
                            event: e as unknown as MouseEvent,
                        });
                        !hit?.phone && e.preventDefault();
                        setTrackingName("list");
                    }}
                    className={classnames(style.iconWrapper, hit?.phone ? "" : style.disabledWrapper)}
                >
                    <Icon
                        color="inherit"
                        size={jnjFullBranded ? "medium" : "xs"}
                        icon={jnjFullBranded ? jnjPhone : mdiPhone}
                        type={jnjFullBranded ? "jnj" : "mdi"}
                    />
                </a>
            </div>
        </div>
    );
};
